import React, { useState, useEffect, useRef } from 'react';
import { IonIcon, IonSpinner } from '@ionic/react';
import { backspaceOutline, checkmarkCircle, closeCircle } from 'ionicons/icons';
import {
  validateDashboardAccessCode,
  getDeviceId,
  setKioskSession,
} from '../services/quickAccess';
import './QuickAccessCodeEntry.css';

interface QuickAccessCodeEntryProps {
  onSuccess: (result: {
    dashboardId: string;
    householdId: string;
    dashboardName?: string;
    codeName?: string;
  }) => void;
  onCancel?: () => void;
}

const KEYPAD_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', 'backspace'];

const QuickAccessCodeEntry: React.FC<QuickAccessCodeEntryProps> = ({
  onSuccess,
  onCancel,
}) => {
  const [digits, setDigits] = useState<string[]>(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [shake, setShake] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  // Focus the appropriate input when focusedIndex changes
  useEffect(() => {
    if (!success && !loading) {
      inputRefs.current[focusedIndex]?.focus();
    }
  }, [focusedIndex, success, loading]);

  // Get the full code string
  const getCode = () => digits.join('');

  // Handle input change for individual fields
  const handleInputChange = (index: number, value: string) => {
    if (loading || success) return;
    setError(null);

    // Only accept single digit
    const digit = value.replace(/\D/g, '').slice(-1);

    if (digit) {
      const newDigits = [...digits];
      newDigits[index] = digit;
      setDigits(newDigits);

      // Auto-advance to next field
      if (index < 5) {
        setFocusedIndex(index + 1);
      }
    }
  };

  // Handle keydown events for navigation
  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (loading || success) return;

    if (e.key === 'Backspace') {
      e.preventDefault();
      const newDigits = [...digits];

      if (digits[index]) {
        // Clear current field
        newDigits[index] = '';
        setDigits(newDigits);
      } else if (index > 0) {
        // Move to previous field and clear it
        newDigits[index - 1] = '';
        setDigits(newDigits);
        setFocusedIndex(index - 1);
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      setFocusedIndex(index - 1);
    } else if (e.key === 'ArrowRight' && index < 5) {
      e.preventDefault();
      setFocusedIndex(index + 1);
    } else if (e.key === 'Escape' && onCancel) {
      onCancel();
    } else if (/^[0-9]$/.test(e.key)) {
      e.preventDefault();
      handleInputChange(index, e.key);
    }
  };

  // Handle paste
  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    if (loading || success) return;

    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData) {
      const newDigits = [...digits];
      for (let i = 0; i < pastedData.length && i < 6; i++) {
        newDigits[i] = pastedData[i];
      }
      setDigits(newDigits);
      setFocusedIndex(Math.min(pastedData.length, 5));
    }
  };

  // Handle keypad button press
  const handleKeyPress = (key: string) => {
    if (loading || success) return;
    setError(null);

    if (key === 'backspace') {
      const newDigits = [...digits];
      // Find last filled digit and clear it
      for (let i = 5; i >= 0; i--) {
        if (newDigits[i]) {
          newDigits[i] = '';
          setDigits(newDigits);
          setFocusedIndex(i);
          break;
        }
      }
    } else {
      // Find first empty slot and fill it
      const emptyIndex = digits.findIndex(d => !d);
      if (emptyIndex !== -1) {
        const newDigits = [...digits];
        newDigits[emptyIndex] = key;
        setDigits(newDigits);
        setFocusedIndex(Math.min(emptyIndex + 1, 5));
      }
    }
  };

  // Auto-submit when all 6 digits are filled
  useEffect(() => {
    const code = getCode();
    if (code.length === 6 && digits.every(d => d)) {
      validateCode(code);
    }
  }, [digits]);

  // Validate the code
  const validateCode = async (code: string) => {
    setLoading(true);
    setError(null);

    try {
      const deviceId = getDeviceId();
      const result = await validateDashboardAccessCode(code, deviceId);

      if (result.success && result.dashboard_id && result.household_id) {
        setSuccess(true);

        // Store kiosk session for persistence
        setKioskSession({
          dashboardId: result.dashboard_id,
          householdId: result.household_id,
          dashboardName: result.dashboard_name || 'Dashboard',
          codeName: result.code_name || 'Access Code',
          validatedAt: new Date().toISOString(),
        });

        // Short delay before calling onSuccess for visual feedback
        setTimeout(() => {
          onSuccess({
            dashboardId: result.dashboard_id!,
            householdId: result.household_id!,
            dashboardName: result.dashboard_name,
            codeName: result.code_name,
          });
        }, 1000);
      } else {
        setError(result.error || 'Invalid code');
        setShake(true);
        setTimeout(() => {
          setShake(false);
          setDigits(['', '', '', '', '', '']);
          setFocusedIndex(0);
        }, 500);
      }
    } catch (err) {
      setError('Failed to validate code');
      setShake(true);
      setTimeout(() => {
        setShake(false);
        setDigits(['', '', '', '', '', '']);
        setFocusedIndex(0);
      }, 500);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="quick-access-entry">
      <div className="entry-header">
        <h2>Enter Access Code</h2>
        <p>Enter the 6-digit code to access a dashboard</p>
      </div>

      {/* Code input fields */}
      <div className={`code-inputs ${shake ? 'shake' : ''} ${success ? 'success' : ''}`}>
        {success ? (
          <div className="success-display">
            <IonIcon icon={checkmarkCircle} />
            <span>Connected!</span>
          </div>
        ) : (
          <div className="input-group">
            {digits.map((digit, index) => (
              <React.Fragment key={index}>
                <input
                  ref={el => inputRefs.current[index] = el}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleInputChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  onFocus={() => setFocusedIndex(index)}
                  className={`code-input ${digit ? 'filled' : ''} ${focusedIndex === index ? 'focused' : ''}`}
                  disabled={loading || success}
                  autoComplete="off"
                />
                {index === 2 && <span className="input-separator">-</span>}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="entry-error">
          <IonIcon icon={closeCircle} />
          <span>{error}</span>
        </div>
      )}

      {/* Loading indicator */}
      {loading && (
        <div className="entry-loading">
          <IonSpinner />
          <span>Validating...</span>
        </div>
      )}

      {/* Numeric keypad */}
      {!success && (
        <div className="keypad">
          {KEYPAD_KEYS.map((key, index) => (
            <button
              key={index}
              className={`keypad-key ${key === '' ? 'empty' : ''} ${key === 'backspace' ? 'backspace' : ''}`}
              onClick={() => key && handleKeyPress(key)}
              disabled={loading || !key}
              type="button"
            >
              {key === 'backspace' ? (
                <IonIcon icon={backspaceOutline} />
              ) : (
                key
              )}
            </button>
          ))}
        </div>
      )}

      {/* Cancel button */}
      {onCancel && !success && (
        <button className="cancel-button" onClick={onCancel} type="button">
          Cancel
        </button>
      )}
    </div>
  );
};

export default QuickAccessCodeEntry;
